import { useState } from 'react';
import WrapperModal from '@/views/elements/WrapperModal';
import { Box, Stack } from '@mui/system';
import { InputAdornment, TextField } from '@mui/material';
import ButtonElement from '@/views/elements/button/ButtonElement';
import { postMasterInfo } from '@/controllers/gridManager/masterTransManager/masterTransManager';
import classNames from 'classnames/bind';
import styles from './EditMasterInfoModal.module.scss';
import ToastUtil from '@/views/components/Toast/Toast';
import NumberInput from '@/views/elements/Inputs/NumberInput';
import { setStateLoadingAction } from '@/stores/dataManager/Actions/appAction';
import { useCosInfo } from '@/controllers/gridManager/accountsManager/accountManager';
import { useTranslation } from 'react-i18next';

const cx = classNames.bind(styles);

interface EditMasterInfoModalProps {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  preData: {
    shortInvestThesis?: string | null;
    investThesis?: string | null;
    minInvestAmt?: number | null;
    feeRate?: number | null;
    commissionRate?: number | null;
  }
}

const EditMasterInfoModal = ({ preData, open, onClose, onDone }: EditMasterInfoModalProps) => {
  preData = {
      shortInvestThesis: preData?.shortInvestThesis ?? '',
      investThesis: preData?.investThesis ?? '',
      minInvestAmt: preData?.minInvestAmt ?? null,
      feeRate: preData?.feeRate ? Number(preData?.feeRate.toFixed(2)) : null,
      commissionRate: preData?.commissionRate ?? null,
  };
  const { t } = useTranslation()
  const [shortInvestThesis, setShortInvestThesis] = useState(preData.shortInvestThesis);
  const [investThesis, setInvestThesis] = useState(preData.investThesis);
  const { cosInfo } = useCosInfo()
  const [minInvestAmt, setMinInvestAmt] = useState(preData.minInvestAmt || null);
  const [commissionRate, setCommissionRate] = useState(preData.commissionRate || null);
  const [feeRate, setFeeRate] = useState(preData.feeRate || null);
  const [changes, setChanges] = useState({
    shortInvestThesisChange: false,
    investThesisChange: false,
    minInvestAmtChange: false,
    feeRateChange: false,
    commissionRateChange: false
  })

  const isChanged = () => {
    const { shortInvestThesisChange,
      investThesisChange,
      minInvestAmtChange,
      feeRateChange,
      commissionRateChange } = changes
    return shortInvestThesisChange || investThesisChange || minInvestAmtChange || commissionRateChange || feeRateChange
  }

  const handleSubmit = async () => {
    setStateLoadingAction(true)
    let response = await postMasterInfo(cosInfo.refaccount, { shortInvestThesis, investThesis, minInvestAmt, commissionRate, feeRate })
    if (response.isSuccess) {
      ToastUtil.success(t('copytrade.portfolioManagement.editSuccess'))
      onDone()
    }
    setStateLoadingAction(false)
  };

  const handleShortInvestThesisChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if ((event.target.value === preData.shortInvestThesis?.toString() && changes.shortInvestThesisChange) ||
      (event.target.value !== preData.shortInvestThesis?.toString() && !changes.shortInvestThesisChange)) setChanges({ ...changes, shortInvestThesisChange: !changes.shortInvestThesisChange })
    setShortInvestThesis(event.target.value)
  };

  const handleInvestThesisChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if ((event.target.value === preData.investThesis?.toString() && changes.investThesisChange) ||
      (event.target.value !== preData.investThesis?.toString() && !changes.investThesisChange)) setChanges({ ...changes, investThesisChange: !changes.investThesisChange })
    setInvestThesis(event.target.value)
  };

  const handleMinInvestAmtChange = (value: number | null) => {
    if ((value === preData.minInvestAmt && changes.minInvestAmtChange) ||
      (value !== preData.minInvestAmt && !changes.minInvestAmtChange)) setChanges({ ...changes, minInvestAmtChange: !changes.minInvestAmtChange })
    setMinInvestAmt(value);
  };

  const handlefeeRateChange = (value: number | null) => {
    if ((value === preData.feeRate && changes.feeRateChange) ||
      (value !== preData.feeRate && !changes.feeRateChange)) setChanges({ ...changes, feeRateChange: !changes.feeRateChange })
    setFeeRate(value);
  };

  const handleCommissionRateChange = (value: number | null) => {
    if ((value === preData.commissionRate && changes.commissionRateChange) ||
      (value !== preData.commissionRate && !changes.commissionRateChange)) setChanges({ ...changes, commissionRateChange: !changes.commissionRateChange })
    setCommissionRate(value);
  };

  return (
    <WrapperModal
      open={open}
      handleClose={onClose}
      title={t('copytrade.portfolioManagement.editModalTitle')}
    >
      <Stack direction="row" spacing={2}>
      </Stack>
      <Stack
        direction={"column"}
        spacing={2}
        alignItems="center"
        sx={{
          "& > *": { width: "100%" },
        }}
      >
        <Box sx={{
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '21px',
          marginBottom: '5px !important'
        }}>
          {t('copytrade.portfolioManagement.shortDesc')}
        </Box>

        <TextField
          value={shortInvestThesis}
          multiline
          onChange={handleShortInvestThesisChange}
          variant="filled"
          size="small"
          helperText={!shortInvestThesis && changes.shortInvestThesisChange && t('copytrade.portfolioManagement.validateMessage')}
          error={!shortInvestThesis && changes.shortInvestThesisChange}
          className={cx("register-master-step1__select")}
        >
        </TextField>
      </Stack>
      <Stack
        direction={"column"}
        spacing={2}
        alignItems="center"
        sx={{
          "& > *": { width: "100%" },
        }}
      >
        <Box sx={{
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '21px',
          marginBottom: '5px !important'
        }}>
          {t('copytrade.portfolioManagement.investThesis')}
        </Box>
        <TextField
          value={investThesis}
          multiline
          onChange={handleInvestThesisChange}
          variant="filled"
          size="small"
          helperText={!investThesis && changes.investThesisChange && t('copytrade.portfolioManagement.validateMessage')}
          error={!investThesis && changes.investThesisChange}
          className={cx("register-master-step1__select")}
        />
      </Stack>
      <Stack
        direction={"column"}
        spacing={2}
        alignItems="center"
        sx={{
          "& > *": { width: "100%" },
        }}
      >
        <Box sx={{
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '21px',
          marginBottom: '5px !important'
        }}>
          {t('copytrade.portfolioManagement.minInvest')}
        </Box>
        <NumberInput
          decimalScale={2}
          value={minInvestAmt}
          onchange={handleMinInvestAmtChange}
          customInput={TextField}
          thousandSeparator
          valueIsNumericString
          variant="filled"
          size="small"
          helperText={minInvestAmt === null && changes.minInvestAmtChange && t('copytrade.portfolioManagement.validateMessage')}
          error={minInvestAmt === null && changes.minInvestAmtChange}
          className={cx("register-master-step1__select")}
          sx={{
            '& input[type="number"]::-webkit-outer-spin-button, & input[type="number"]::-webkit-inner-spin-button': {
              display: 'none',
            }
          }}
          inputProps={{ min: 0 }}
          InputProps={{
            endAdornment: <InputAdornment position="end" sx={{ marginRight: '4px', lineHeight: '21px' }}>VNĐ</InputAdornment>,
          }}
        />
      </Stack>
      <Stack
        direction={"column"}
        spacing={2}
        alignItems="center"
        sx={{
          "& > *": { width: "100%" },
        }}
      >
        <Box sx={{
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '21px',
          marginBottom: '5px !important'
        }}>
          {t('copytrade.portfolioManagement.bonusFee')}
        </Box>
        <NumberInput
          decimalScale={2}
          value={feeRate}
          onchange={handlefeeRateChange}
          customInput={TextField}
          thousandSeparator
          valueIsNumericString
          variant="filled"
          size="small"
          helperText={feeRate === null && changes.feeRateChange && t('copytrade.portfolioManagement.validateMessage')}
          error={feeRate === null && changes.feeRateChange}
          className={cx("register-master-step1__select")}
          sx={{
            '& input[type="number"]::-webkit-outer-spin-button, & input[type="number"]::-webkit-inner-spin-button': {
              display: 'none',
            }
          }}
          inputProps={{ min: 0 }}
          InputProps={{
            endAdornment: <InputAdornment position="end" sx={{ marginRight: '4px', lineHeight: '21px' }}>%</InputAdornment>,
          }}
        />
      </Stack>
      <Stack
        direction={"column"}
        spacing={2}
        alignItems="center"
        sx={{
          "& > *": { width: "100%" },
        }}
      >
        <Box sx={{
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '21px',
          marginBottom: '5px !important'
        }}>
          {t('copytrade.portfolioManagement.commissionRate')}
        </Box>
        <NumberInput
          decimalScale={2}
          value={commissionRate}
          onchange={handleCommissionRateChange}
          customInput={TextField}
          thousandSeparator
          valueIsNumericString
          variant="filled"
          size="small"
          helperText={commissionRate === null && changes.commissionRateChange && t('copytrade.portfolioManagement.validateMessage')}
          error={commissionRate === null && changes.commissionRateChange}
          className={cx("register-master-step1__select")}
          sx={{
            '& input[type="number"]::-webkit-outer-spin-button, & input[type="number"]::-webkit-inner-spin-button': {
              display: 'none',
            }
          }}
          inputProps={{ min: 0 }}
          InputProps={{
            endAdornment: <InputAdornment position="end" sx={{ marginRight: '4px', lineHeight: '21px' }}>%</InputAdornment>,
          }}
        />
      </Stack>
      <Stack direction={"row"} spacing={2} justifyContent={"center"}>
        <ButtonElement
          variant="contained"
          onClick={onClose}
          customType='Secondary'
          sx={{ width: '100%', fontSize: '14px', fontWeight: '500', lineHeight: '21px' }}
        >
          {t('copytrade.common.close')}
        </ButtonElement>
        <ButtonElement
          variant="contained"
          disabled={!isChanged()}
          onClick={handleSubmit}
          sx={{ width: '100%', fontSize: '14px', fontWeight: '500', lineHeight: '21px' }}
        >
          {t('copytrade.common.save')}
        </ButtonElement>
      </Stack>
    </WrapperModal>
  );
};

export default EditMasterInfoModal;